// TanStack Query hooks: one per API read. Pages only talk to the API through these.

import { adminPath, useScope } from "../lib/scope";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, HttpError, qs, rememberOwner, repoPath } from "./client";
import {
  ACTIVE_STATES,
  type Actor, type AuditEntry, type AuthProvider, type Change, type ChangeComment, type ChangeState, type Decision, type CiArtifact, type Overlap, type CiOverview, type CiRun, type CommitView, type DiffFile, type Evidence, type FileView,
  type Attention, type CodeGraph, type DocCheck, type Knowledge, type KnowledgeDoc, type KnowledgeHit, type LogEntry, type Me, type OrgDetail, type Policy, type ProviderRepo, type QueueStats, type RepoConnection, type RepoGrant, type RepoInfo, type RepoRow,
  type RepoStats, type RunnersView, type TreeEntry, type RemoteStatus, type Intent, type IntentView,
} from "./types";

export const useWhoami = () => useQuery({ queryKey: ["whoami"], queryFn: () => api.get<{ actor: Actor | null }>("/v1/whoami").then((r) => r.actor), staleTime: 60_000 });

/** Sign-in providers; works signed out. */
export const useProviders = () =>
  useQuery({ queryKey: ["providers"], queryFn: () => api.get<{ providers: AuthProvider[] }>("/auth/providers").then((r) => r.providers), staleTime: 300_000 });

/** How this server hands out the CLI; works signed out. */
export const useInstallInfo = () =>
  useQuery({ queryKey: ["install-info"], queryFn: () => api.get<{ dev: boolean; platforms: string[] }>("/install.json"), staleTime: Infinity, retry: false });

/** The signed-in person and their connections (none for token sign-ins). */
export const useMe = (enabled = true) => useQuery({ queryKey: ["me"], queryFn: () => api.get<Me>("/v1/me"), staleTime: 30_000, retry: false, enabled });

export const useOrg = (name: string) =>
  useQuery({ queryKey: ["org", name], queryFn: () => api.get<{ org: OrgDetail }>(`/v1/orgs/${encodeURIComponent(name)}`).then((r) => r.org), retry: false });

export const useConnectionRepos = (connection: string | null, q: string) =>
  useQuery({
    queryKey: ["connection-repos", connection, q],
    queryFn: () => api.get<{ repos: ProviderRepo[] }>(`/v1/me/connections/${connection}/repos${qs({ q })}`).then((r) => r.repos),
    enabled: !!connection,
    staleTime: 60_000,
    retry: false,
    placeholderData: (prev) => prev,
  });

export const useRepoAccess = (repo: string, enabled = true) =>
  useQuery({ queryKey: ["repo-access", repo], queryFn: () => api.get<{ access: RepoGrant[] }>(repoPath(repo, "/access")).then((r) => r.access), retry: false, enabled });

export const useRepoConnection = (repo: string) =>
  useQuery({ queryKey: ["repo-connection", repo], queryFn: () => api.get<{ connection: RepoConnection | null }>(repoPath(repo, "/connection")).then((r) => r.connection) });

export const useRepos = () =>
  useQuery({ queryKey: ["repos"], queryFn: () => api.get<{ repos: Array<RepoRow & { info: RepoInfo | null }> }>("/v1/repos").then((r) => {
      for (const x of r.repos) rememberOwner(x.slug, x.owner);
      return r.repos;
    }) });

export const useRepo = (repo: string) =>
  useQuery({
    queryKey: ["repo", repo],
    queryFn: () => api.get<{ repo: RepoRow; info: RepoInfo }>(repoPath(repo)).then((r) => {
        rememberOwner(repo, r.repo.owner);
        return r;
      }),
    refetchInterval: (q) => (q.state.data?.repo.import_state === "pending" ? 2_000 : 15_000),
    staleTime: 15_000,
  });

export const useTree = (repo: string, commit: string, path: string) =>
  useQuery({
    queryKey: ["tree", repo, commit, path],
    queryFn: () => api.get<{ commit: string; entries: TreeEntry[] }>(repoPath(repo, `/tree${qs({ commit, path })}`)),
    retry: false,
  });

/** Every path in the commit, for "Go to file": fetched only once someone starts typing. */
export const useTreeAll = (repo: string, commit: string, enabled: boolean) =>
  useQuery({
    queryKey: ["tree-all", repo, commit],
    queryFn: () => api.get<{ commit: string; entries: TreeEntry[] }>(repoPath(repo, `/tree${qs({ commit, depth: 20 })}`)),
    enabled,
    staleTime: 60_000,
  });

export const useFile = (repo: string, commit: string, path: string, enabled = true) =>
  useQuery({
    queryKey: ["file", repo, commit, path],
    queryFn: () => api.get<FileView>(repoPath(repo, `/file${qs({ commit, path })}`)),
    enabled,
    retry: false,
  });

export const useLog = (repo: string, opts: { path?: string; limit?: number; cursor?: string } = {}) =>
  useQuery({
    queryKey: ["log", repo, opts],
    queryFn: () => api.get<{ entries: LogEntry[]; cursor: string | null }>(repoPath(repo, `/log${qs(opts)}`)),
  });

export const useCommit = (repo: string, sha: string) =>
  useQuery({ queryKey: ["commit", repo, sha], queryFn: () => api.get<CommitView>(repoPath(repo, `/commits/${sha}?diff=unified`)), staleTime: Infinity });

export const useDiff = (repo: string, from: string | null, to: string | null) =>
  useQuery({
    queryKey: ["diff", repo, from, to],
    queryFn: () => api.get<{ files: DiffFile[]; additions: number; deletions: number }>(repoPath(repo, `/diff${qs({ from, to, format: "unified" })}`)),
    enabled: !!to,
    staleTime: Infinity,
  });

export const useChanges = (repo: string, state: string, enabled = true) =>
  useQuery({
    queryKey: ["changes", repo, state],
    queryFn: () => api.get<{ changes: Change[]; counts: Record<string, number> }>(repoPath(repo, `/changes${qs({ state, limit: 100 })}`)),
    refetchInterval: 5_000,
    enabled,
  });

export const useChange = (repo: string, id: string) =>
  useQuery({
    queryKey: ["change", repo, id],
    // comments is absent on servers without comment support.
    queryFn: () => api.get<{ change: Change; evidence: Evidence[]; audit: AuditEntry[]; comments?: ChangeComment[] }>(repoPath(repo, `/changes/${id}?verbose=1`)),
    refetchInterval: (q) => (q.state.data && ACTIVE_STATES.includes(q.state.data.change.state) ? 2_000 : 15_000),
  });

/** What people asked for, newest first, with their state. */
export const useIntents = (repo: string) =>
  useQuery({
    queryKey: ["intents", repo],
    queryFn: () => api.get<{ intents: Intent[] }>(repoPath(repo, `/intents${qs({ limit: 200 })}`)).then((r) => r.intents),
    refetchInterval: 10_000,
    retry: false, // a server without intents: the page says so
  });

/** One intent with its spec, outcomes and changes; reading it derives its state afresh. */
export const useIntent = (repo: string, id: string) =>
  useQuery({
    queryKey: ["intent", repo, id],
    queryFn: () => api.get<{ intent: IntentView }>(repoPath(repo, `/intents/${id}`)).then((r) => r.intent),
    refetchInterval: 5_000,
  });

/** Verdict, next step, gates and attempts, polled as often as the change itself. */
export const useDecision = (repo: string, id: string, state?: ChangeState) =>
  useQuery({
    queryKey: ["change", repo, id, "decision", state],
    queryFn: () => api.get<{ decision: Decision }>(repoPath(repo, `/changes/${id}/decision`)).then((r) => r.decision),
    refetchInterval: state && ACTIVE_STATES.includes(state) ? 2_000 : 15_000,
    retry: false, // a server without /decision: the page falls back to its own status
  });

/** An agent's recent changes in any state, newest first: the attempts behind a task. */
export const useAgentChanges = (repo: string, agent: string, enabled = true) =>
  useQuery({
    queryKey: ["changes", repo, "agent", agent],
    queryFn: () => api.get<{ changes: Change[] }>(repoPath(repo, `/changes${qs({ agent, limit: 100 })}`)).then((r) => r.changes),
    refetchInterval: 15_000,
    enabled,
  });

/** Other work on a change's files: bee plan's overlaps, without the change itself. */
export const useOverlaps = (repo: string, c: Change, enabled: boolean) =>
  useQuery({
    queryKey: ["overlaps", repo, c.id],
    queryFn: () =>
      api
        .send<{ overlaps: Overlap[] }>("POST", repoPath(repo, "/plan"), { paths: c.paths, base: c.base_sha, task: c.task_id ?? "" })
        .then((r) => r.overlaps.filter((o) => o.change_id !== c.id)),
    enabled: enabled && c.paths.length > 0,
    refetchInterval: 15_000,
    retry: false,
  });

/** Files that usually land together with these paths. */
export const useRelated = (repo: string, paths: string[], enabled = true) =>
  useQuery({
    queryKey: ["related", repo, paths],
    queryFn: () =>
      api.get<{ related: Array<{ path: string; together: number; ratio: number }> }>(repoPath(repo, `/related?${paths.map((p) => `paths=${encodeURIComponent(p)}`).join("&")}&limit=10`)).then((r) => r.related),
    enabled: enabled && paths.length > 0 && paths.length <= 200,
    staleTime: 300_000,
    retry: false,
  });

export const useCi = (repo: string) =>
  useQuery({ queryKey: ["ci", repo], queryFn: () => api.get<CiOverview>(repoPath(repo, "/ci")), refetchInterval: 5_000 });

export const useStats = (repo: string, hours: number) =>
  useQuery({ queryKey: ["stats", repo, hours], queryFn: () => api.get<RepoStats>(repoPath(repo, `/stats${qs({ hours })}`)), refetchInterval: 5_000 });

export const useCiRun = (repo: string, subject: string, commit: string) =>
  useQuery({
    queryKey: ["cirun", repo, subject, commit],
    queryFn: () => api.get<CiRun>(repoPath(repo, `/ci/run${qs({ subject, commit })}`)),
    refetchInterval: (q) => (q.state.data?.jobs.some((e) => e.status === "queued" || e.status === "waiting") ? 2_000 : false),
  });

/** A job log; while the job runs, polls for what the runner has streamed since the last read. */
export interface CiLogData {
  text: string;
  size: number;
  complete: boolean;
  decoder: TextDecoder;
}

export const useCiLog = (repo: string, change: string, job: string, attempt: number) => {
  const client = useQueryClient();
  const key = ["cilog", repo, change, job, attempt];
  return useQuery({
    queryKey: key,
    queryFn: async (): Promise<CiLogData> => {
      const prev = client.getQueryData<CiLogData>(key);
      const offset = prev && !prev.complete ? prev.size : 0;
      const r = await api.bytes(repoPath(repo, `/ci/log${qs({ change, job, attempt, offset })}`));
      const complete = r.headers.get("x-bee-log-complete") !== "0";
      const size = Number(r.headers.get("x-bee-log-size") ?? r.body.length);
      // The finished log replaces the live one from its start.
      const base = prev && !prev.complete && !complete ? prev : null;
      const decoder = base?.decoder ?? new TextDecoder();
      const text = (base?.text ?? "") + decoder.decode(r.body, { stream: !complete });
      return { text, size, complete, decoder };
    },
    refetchInterval: (q) => (q.state.data && !q.state.data.complete ? 1_500 : false),
    staleTime: (q) => (q.state.data?.complete ? Infinity : 0),
  });
};

export const useCiArtifacts = (repo: string, change: string, job: string, attempt: number, enabled: boolean) =>
  useQuery({
    queryKey: ["ciartifacts", repo, change, job, attempt],
    queryFn: () =>
      api
        .get<{ artifact: CiArtifact }>(repoPath(repo, `/ci/artifacts${qs({ change, job, attempt, meta: 1 })}`))
        .then((r) => r.artifact)
        .catch((e: unknown) => (e instanceof HttpError && e.status === 404 ? null : Promise.reject(e))),
    enabled,
  });

/** action: comma-separated action prefixes; actor: exact subject. */
export type AuditFilter = { cursor?: number; action?: string; actor?: string };

export const AUDIT_PAGE = 100;

export const useAudit = (repo: string, f: AuditFilter = {}, enabled = true) =>
  useQuery({
    queryKey: ["audit", repo, f],
    queryFn: () => api.get<{ entries: AuditEntry[] }>(repoPath(repo, `/audit${qs({ ...f, limit: AUDIT_PAGE })}`)).then((r) => r.entries),
    enabled,
  });

export const useRemote = (repo: string) =>
  useQuery({
    queryKey: ["remote", repo],
    queryFn: () => api.get<RemoteStatus>(repoPath(repo, "/remote")),
    refetchInterval: (q) => {
      const d = q.state.data;
      return d?.remote && (d.active || d.remote.checked_at == null) ? 5_000 : 30_000;
    },
  });

export const usePolicy = (repo: string) => useQuery({ queryKey: ["policy", repo], queryFn: () => api.get<{ policy: Policy }>(repoPath(repo, "/policy")).then((r) => r.policy) });

/** The job queue of the organization whose settings are open. */
export const useQueue = () => {
  const org = useScope();
  return useQuery({ queryKey: ["queue", "scope", org], queryFn: () => api.get<QueueStats>(adminPath(org, "/queue")), refetchInterval: 3_000 });
};

/** This repository's jobs only; polls fast while anything is queued or running, slowly when idle. */
export const useRepoQueue = (repo: string) =>
  useQuery({
    queryKey: ["queue", repo],
    queryFn: () => api.get<QueueStats>(repoPath(repo, "/queue")),
    refetchInterval: (q) => (q.state.data?.jobs.some((j) => j.n > 0) ? 3_000 : 20_000),
  });

export const useRunners = (refetchInterval = 10_000) => {
  const org = useScope();
  return useQuery({ queryKey: ["runners", org], queryFn: () => api.get<RunnersView>(adminPath(org, "/runners")), refetchInterval, retry: false });
};

/** The activity of every repository an organization holds. */
export const useOrgAudit = (org: string, f: AuditFilter = {}, enabled = true) =>
  useQuery({
    queryKey: ["oaudit", org, f],
    queryFn: () => api.get<{ entries: AuditEntry[] }>(`/v1/orgs/${encodeURIComponent(org)}/audit${qs({ ...f, limit: AUDIT_PAGE })}`).then((r) => r.entries),
    retry: false,
    enabled,
  });

/** Admin/agent mutations: POST to a path, then refresh everything for the repo. */
export function useAction(repo?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ method = "POST", path, body }: { method?: string; path: string; body: unknown }) => api.send<Record<string, unknown>>(method, path, body),
    onSuccess: () => qc.invalidateQueries({ predicate: (q) => !repo || q.queryKey.includes(repo) || q.queryKey[0] === "repos" }),
  });
}

/** The file reference graph at a commit: around `focus`, or the top files under `paths`. */
export const useCodeGraph = (repo: string, opts: { commit?: string; focus?: string; paths?: string; limit?: number }) =>
  useQuery({
    queryKey: ["graph", repo, opts],
    queryFn: () => api.get<CodeGraph>(repoPath(repo, `/graph${qs(opts)}`)),
    retry: false,
  });

/** The repo's knowledge graph, read from its Markdown files at main. */
export const useKnowledge = (repo: string) =>
  useQuery({ queryKey: ["knowledge", repo], queryFn: () => api.get<Knowledge>(repoPath(repo, "/knowledge/docs")), retry: false });

/** Full-text search over the knowledge documents; idle until there are two characters. */
export const useKnowledgeSearch = (repo: string, q: string) =>
  useQuery({
    queryKey: ["knowledge-search", repo, q],
    queryFn: () => api.get<{ hits: KnowledgeHit[] }>(repoPath(repo, `/knowledge/search?q=${encodeURIComponent(q)}`)).then((r) => r.hits),
    enabled: q.trim().length >= 2,
    retry: false,
  });

/** Checks that need a person, drafts and plans in force; null on a server without checks yet. */
export const useAttention = (repo: string) =>
  useQuery({
    queryKey: ["knowledge-attention", repo],
    queryFn: () =>
      api.get<Attention>(repoPath(repo, "/knowledge/attention")).catch((e: unknown) => (e instanceof HttpError && e.status === 404 ? null : Promise.reject(e))),
    retry: false,
  });

/** Every check in force with its state; null on a server without checks yet. */
export const useChecks = (repo: string) =>
  useQuery({
    queryKey: ["knowledge-checks", repo],
    queryFn: () =>
      api
        .get<{ commit: string; checks: DocCheck[] }>(repoPath(repo, "/knowledge/checks"))
        .then((r) => r.checks)
        .catch((e: unknown) => (e instanceof HttpError && e.status === 404 ? null : Promise.reject(e))),
    retry: false,
  });

/** One knowledge document and its Markdown. */
export const useKnowledgeDoc = (repo: string, path: string | null) =>
  useQuery({
    queryKey: ["knowledge-doc", repo, path],
    queryFn: () => api.get<{ doc: KnowledgeDoc; body: string }>(repoPath(repo, `/knowledge/doc?path=${encodeURIComponent(path ?? "")}`)),
    enabled: !!path,
    retry: false,
  });
